/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import config from '../../../../share/config';
import error from '../../../../common/nodejs/errors';
import cstmTable from '../../../../share/components/vue/PaginationTable';

/** 各フェーズ定義 */
const PHASE = {
  CONFIRM_START: 'confirmStart',
  CONFIRM_BUMPER_CHANGE: 'confirmBumperChange',
  CONFIRM_NEW_BUMPER_CHANGE: 'confirmNewBumperChange',
  EXECUTING: 'executing',
  SUCCESS: 'success',
  SUCCESS_CONFIRM: 'successConfirm',
  CONFIRM_THRESHOLD: 'confirmThreshold',
  FAILED_EXECUTE: 'failedExecute',
  FAILED_ENVIRONMENT: 'failedEnvironment',
  FAILED: 'failed'
};

export default {
  name: 'requirement087',
  data() {
    return {
      phase: PHASE.CONFIRM_START,
      isLoading: false,
      isDisabled: false,
      displayInfoThreshold: '', // レーダ反射強度閾値表示画面メッセージ
      displayInfoDeg: '',       // 点検失敗エラー画面の軸ずれ量メッセージ
      modal_requirement_087: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  components: {
    'cstm-table': cstmTable,
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.CONFIRM_START:
        case PHASE.CONFIRM_BUMPER_CHANGE:
        case PHASE.CONFIRM_NEW_BUMPER_CHANGE:
        case PHASE.EXECUTING:
          return 'secondary';
        case PHASE.SUCCESS:
        case PHASE.SUCCESS_CONFIRM:
          return 'success';
        case PHASE.CONFIRM_THRESHOLD:
          return 'secondary';
        case PHASE.FAILED_EXECUTE:
        case PHASE.FAILED_ENVIRONMENT:
        case PHASE.FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    },
  },

  mounted() {
    // 初期化メソッドを呼び出す
    this.init();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化メソッド */
    async init() {
      this.modal_requirement_087 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // 終了処理エラー判定フラグ(初期状態はエラー判定無し)
      this.isErrorCheck = false;
      // 反射強度確認要否フラグ(初期状態は否)
      this.isReflectionIntensityConfirm = false;
    },

    //----- htmlイベントメソッド -----------------------------------------------//

    /** 画面を閉じる（終了処理を実行する）ボタンクリックイベント */
    async clickClose() {
      // 終了判定結果の取得
      if (await this.exitPostGet()) {
        this.$emit('close-wizard');
        this.modal_requirement_087 = false;
      }
    },

    /** 開始確認画面OKボタンクリックイベント */
    async clickConfirmBumperChange() {
      // バンパー交換確認画面を表示
      this.showConfirmBumperChange();
    },

    /** バンパー交換確認画面OKボタンクリックイベント */
    async clickConfirmNewBumperChange() {
      // 新品バンパー交換確認画面を表示
      this.showConfirmNewBumperChange();
    },

    /**
     * ルーチン実行遷移ボタンクリックイベント
     * @param value 反射強度確認要否フラグ(true：必要、false：不要)
     */
    async clickExecuting(value) {
      // 本遷移処理以降は終了処理でエラー判定を行う
      this.isErrorCheck = true;
      // 反射強度確認要否フラグを内部保持
      this.isReflectionIntensityConfirm = value;
      // 実行監視画面を表示
      this.showExecuting();
      // ルーチン実行メソッドを呼び出す
      await this.executePostGet();
    },

    /** レーダ反射強度確認画面OKボタンクリックイベント */
    async clickConfirmThreshold() {
      // レーダ反射強度閾値表示画面を表示
      this.showConfirmThreshold();
    },

    //----- PHASE操作メソッド -----------------------------------------------//

    /** バンパー交換確認表示メソッド */
    showConfirmBumperChange() {
      this.phase = PHASE.CONFIRM_BUMPER_CHANGE;
    },

    /** 新品バンパー交換確認表示メソッド */
    showConfirmNewBumperChange() {
      this.phase = PHASE.CONFIRM_NEW_BUMPER_CHANGE;
    },

    /** 実行監視表示メソッド */
    showExecuting() {
      this.phase = PHASE.EXECUTING;
    },

    /** 正常範囲表示メソッド */
    showSuccess() {
      this.phase = PHASE.SUCCESS;
    },

    /** レーダ反射強度確認表示メソッド */
    showConfirmSuccess() {
      this.phase = PHASE.SUCCESS_CONFIRM;
    },

    /** レーダ反射強度閾値表示メソッド */
    showConfirmThreshold() {
      this.phase = PHASE.CONFIRM_THRESHOLD;
    },

    /** 点検失敗エラー表示メソッド */
    showFailedExecute() {
      this.phase = PHASE.FAILED_EXECUTE;
    },

    /** 点検環境不備エラー表示メソッド */
    showFailedEnvironment() {
      this.phase = PHASE.FAILED_ENVIRONMENT;
    },

    /** 共通エラー表示メソッド */
    showFailed() {
      this.phase = PHASE.FAILED;
    },

    //----- バックエンドAPI実行メソッド -----------------------------------------------//

    /**
     * ルーチン実行メソッド
     */
    async executePostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // データ読み出しメソッドを呼び出す
            await this.readPostGet(PHASE.SUCCESS);
          } else {
            // [成功以外]
            switch (response.data.async_code) {
              case error.WSP_EXECUTION_RADAR_CHECK_FAILURE_ERROR:
                // [点検失敗エラー]
                // データ読み出しメソッドを呼び出す
                await this.readPostGet(PHASE.FAILED_EXECUTE);
                break;
              case error.WSP_EXECUTION_RADAR_CHECK_ENVIRONMENT_FAILURE_ERROR:
                // [点検環境不備エラー]
                // 点検環境不備エラー画面に遷移
                this.showFailedEnvironment();
                break;
              default:
                // [上記以外のエラー]
                // 共通エラー画面に遷移
                this.showUnexpectedError();
                break;
            }
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * データ読み出しメソッド
     * ルーチン実行結果が成功、または、点検失敗エラーの場合に呼び出す
     * @param {string} phase 遷移先フェーズ
     */
    async readPostGet(phase) {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            switch (phase) {
              case PHASE.SUCCESS:
                // 反射強度確認要否により分岐
                // HACK: Phase4Step2-2終了間際にESLintのルールが変更になり、ロジックは変更出来なかったため回避。
                /* eslint-disable max-depth */
                if (!this.isReflectionIntensityConfirm) {
                  // 正常範囲表示に遷移
                  this.showSuccess();
                } else {
                  // 読み出した閾値(単位: dB)を画面メッセージとして設定
                  this.displayInfoThreshold = this.$t(`${this.i18nWsKey}.REQ_WS_87_TXT_7`).replace('$DID$', response.data.user_data.db);
                  // レーダ反射強度確認表示に遷移
                  this.showConfirmSuccess();
                }
                break;
              case PHASE.FAILED_EXECUTE:
                // 軸ずれ量を画面メッセージとして設定
                this.displayInfoDeg = this.$t(`${this.i18nWsKey}.REQ_WS_87_TXT_8`).replace('$DID$', response.data.user_data.deg);
                // 点検失敗エラー画面に遷移
                this.showFailedExecute();
                break;
            }
          } else {
            // [成功以外]
            // 共通エラー画面に遷移
            this.showUnexpectedError();
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * 終了処理
     * @return 終了判定フラグ
     */
    async exitPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; if (this.isErrorCheck) this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (this.isErrorCheck) {
          // 終了処理のエラー判定を行う場合
          if (response) {
            // レスポンスが取得できた場合
            if (response.data.status === config.STATUS_WSP_COMPLETED) {
              // [成功]
              return true;
            } else {
              // [成功以外]
              // 共通エラー画面に遷移
              this.showUnexpectedError();
              return false;
            }
          } else {
            // レスポンスが取得できない場合、コールバックでエラー画面に遷移
            return false;
          }
        } else {
          return true;
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; if (this.isErrorCheck) this.showUnexpectedError(); },
      );
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 共通エラー画面では終了処理でエラー判定を行わない
      this.isErrorCheck = false;
      // 共通エラー画面に遷移
      this.showFailed();
    },

  }
};